// src/pages/Login.tsx
import React, { useState, useActionState, startTransition } from "react";
import { Card, Form, Input, Button, Typography, Space, Alert, Divider, App as AntdApp } from 'antd';
import { MailOutlined, LockOutlined, SafetyOutlined } from "@ant-design/icons";
import { loginStart, verifyTotpLogin, sendRecoveryCode, verifyRecoveryLogin } from "../services/auth";
import { useNavigate, useLocation } from "react-router-dom";

// recovery — нет телефона с аутентификатором: код из письма на почту админа (31.10.2026)
type Step = "credentials" | "verify" | "recovery";

function toErrorMessage(err: unknown): string {
  if (err instanceof Error && err.message) return err.message;
  if (typeof err === "string") return err;
  try { return JSON.stringify(err); } catch { return "Неизвестная ошибка"; }
}

const Login: React.FC = () => {
  const [step, setStep] = useState<Step>("credentials");
  const [error, setError] = useState<string | null>(null);
  const [recoveryEmail, setRecoveryEmail] = useState("");
  const [sendingCode, setSendingCode] = useState(false);
  const { message } = AntdApp.useApp();

  const navigate = useNavigate();
  const location = useLocation() as any;
  const redirectTo = location?.state?.from?.pathname || "/";

  // Шаг 1: логин/пароль
  const [, loginAction, pendingLogin] = useActionState(
    async (_prev: null, vals: { email: string; password: string }) => {
      try {
        setError(null);
        const res = await loginStart(vals.email.trim(), vals.password);

        if (!res.require2fa) {
          message.success("Добро пожаловать");
          navigate(redirectTo, { replace: true });
          return null;
        }

        // 2FA включена — переходим к вводу кода Google Authenticator
        setStep("verify");
        return null;
      } catch (err: unknown) {
        setError(toErrorMessage(err) || "Не удалось войти");
        return null;
      }
    },
    null
  );

  // Шаг 2: код Google Authenticator
  const [, verifyAction, pendingVerify] = useActionState(
    async (_prev: null, vals: { totp?: string }) => {
      try {
        setError(null);
        if (!vals.totp) {
          setError("Введите код Google Authenticator");
          return null;
        }
        await verifyTotpLogin(vals.totp.trim());
        message.success("Добро пожаловать");
        navigate(redirectTo, { replace: true });
        return null;
      } catch (err: unknown) {
        setError(toErrorMessage(err) || "Проверка не пройдена");
        return null;
      }
    },
    null
  );

  // Нет телефона с аутентификатором — код на почту админа
  const requestRecoveryCode = async () => {
    try {
      setError(null);
      setSendingCode(true);
      const { email } = await sendRecoveryCode();
      setRecoveryEmail(email);
      setStep("recovery");
    } catch (err: unknown) {
      setError(toErrorMessage(err) || "Не удалось отправить код");
    } finally {
      setSendingCode(false);
    }
  };

  // Шаг 2б: код из письма
  const [, recoveryAction, pendingRecovery] = useActionState(
    async (_prev: null, vals: { code?: string }) => {
      try {
        setError(null);
        await verifyRecoveryLogin((vals.code ?? "").trim());
        message.warning("Вы вошли по коду из почты. Привяжите новый телефон: Профиль → Двухфакторная аутентификация", 12);
        navigate(redirectTo, { replace: true });
        return null;
      } catch (err: unknown) {
        setError(toErrorMessage(err) || "Проверка не пройдена");
        return null;
      }
    },
    null
  );

  const subtitle =
    step === "credentials" ? "Укажите e-mail и пароль"
    : step === "verify" ? "Введите код из Google Authenticator"
    : "Введите код из письма";

  return (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "linear-gradient(180deg,#f6f9ff,#fff)" }}>
      <Card
        style={{ width: 400, boxShadow: "0 10px 30px rgba(17,24,39,.08)", borderRadius: 16 }}
        styles={{ body: { padding: 24 } }}>
        <Space direction="vertical" size={4} style={{ width: "100%" }}>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Вход в админ-панель
          </Typography.Title>
          <Typography.Text type="secondary">{subtitle}</Typography.Text>
        </Space>

        {error && <Alert style={{ marginTop: 12 }} type="error" showIcon message={error} />}

        {step === "credentials" ? (
          <Form
            layout="vertical"
            style={{ marginTop: 16 }}
            onFinish={(vals) => startTransition(() => loginAction(vals as { email: string; password: string }))}
          >
            <Form.Item
              name="email"
              label="E-mail"
              rules={[
                { required: true, message: "Введите e-mail" },
                { type: "email", message: "Некорректный e-mail" },
              ]}
            >
              <Input prefix={<MailOutlined />} placeholder="you@example.com" autoComplete="email" />
            </Form.Item>

            <Form.Item
              name="password"
              label="Пароль"
              rules={[{ required: true, message: "Введите пароль" }]}
            >
              <Input.Password prefix={<LockOutlined />} placeholder="Пароль" autoComplete="current-password" />
            </Form.Item>

            <Button type="primary" htmlType="submit" block loading={pendingLogin}>
              Войти
            </Button>
          </Form>
        ) : step === "recovery" ? (
          <Form
            layout="vertical"
            style={{ marginTop: 16 }}
            onFinish={(vals) => startTransition(() => recoveryAction(vals as { code?: string }))}
            initialValues={{ code: "" }}
          >
            <Typography.Paragraph type="secondary">
              Код из 8 цифр отправлен на {recoveryEmail || "почту админа"}. Действует 10 минут.
            </Typography.Paragraph>
            <Form.Item
              name="code"
              label="Код из письма"
              rules={[
                { required: true, message: "Введите код из письма" },
                { pattern: /^\d{8}$/, message: "Код состоит из 8 цифр" },
              ]}
            >
              <Input
                prefix={<MailOutlined />}
                placeholder="12345678"
                inputMode="numeric"
                maxLength={8}
                autoComplete="one-time-code"
                autoFocus
              />
            </Form.Item>

            <Divider style={{ margin: "8px 0 16px" }} />

            <Space direction="vertical" style={{ width: "100%" }}>
              <Button type="primary" htmlType="submit" block loading={pendingRecovery}>
                Войти по коду из письма
              </Button>
              <Button block onClick={requestRecoveryCode} loading={sendingCode} disabled={pendingRecovery}>
                Отправить код ещё раз
              </Button>
              <Button type="link" block onClick={() => { setError(null); setStep("verify"); }} disabled={pendingRecovery}>
                Назад к коду из приложения
              </Button>
            </Space>
          </Form>
        ) : (
          <Form
            layout="vertical"
            style={{ marginTop: 16 }}
            onFinish={(vals) => startTransition(() => verifyAction(vals as { totp?: string }))}
            initialValues={{ totp: "" }}
          >
            <Form.Item
              name="totp"
              label="Код Google Authenticator"
              rules={[
                { required: true, message: "Введите 6-значный код" },
                { pattern: /^\d{6}$/, message: "Код состоит из 6 цифр" },
              ]}
            >
              <Input
                prefix={<SafetyOutlined />}
                placeholder="123456"
                inputMode="numeric"
                maxLength={6}
                autoComplete="one-time-code"
                autoFocus
              />
            </Form.Item>

            <Divider style={{ margin: "8px 0 16px" }} />

            <Space direction="vertical" style={{ width: "100%" }}>
              <Button type="primary" htmlType="submit" block loading={pendingVerify}>
                Подтвердить вход
              </Button>
              <Button block onClick={() => setStep("credentials")} disabled={pendingVerify}>
                Назад
              </Button>
              <Button type="link" block onClick={requestRecoveryCode} loading={sendingCode} disabled={pendingVerify}>
                Нет доступа к телефону? Код на почту
              </Button>
            </Space>
          </Form>
        )}
      </Card>
    </div>
  );
};

export default Login;
